import {
  pathOr,
  compose,
  map,
  reduce,
  reduceBy,
  mergeAll,
  concat,
  path,
  values,
  mapObjIndexed,
  head,
  intersection,
  prop,
  uniq,
} from "ramda";

import {
  variantById,
  productDetailByHandle,
  productsByTitle,
  productById,
} from "./shopify-utils.shopify.gql";

import getShopifyGlobalId from "./get-shopify-global-id";
import { getProduct, getVariant } from "./normalize-product";
import hasSingleSize from "./has-single-size";
import groupByProp from "./group-by-prop";

// create a map of variantIds and option values
// optionName-OptionValue: [variantIds...]
// Comfort Feel Left- Plush : [34762584293420, 34762556145708]
const createVariantOptionsMap = (options, variants, withOptionName = false) => {
  const groupByOption = optionName =>
    compose(
      concat(withOptionName ? `${optionName}-` : ""),
      pathOr("", ["selectedOptions", optionName, "value"])
    );

  const byOption = reduceBy((acc, { id }) => acc.concat(id), []);
  const groupFuncs = map(
    compose(byOption, groupByOption, path(["name"])),
    options
  );
  return mergeAll(map(f => f(variants), groupFuncs));
};

export const getTilesInfo = (options, variants) => {
  const variantOptionsMap = createVariantOptionsMap(options, variants, true);

  return selectedOptionsObj => {
    const selectedOptions = values(
      mapObjIndexed((value, key) => `${key}-${value}`, selectedOptionsObj)
    );
    const selectedOptionsValues = values(selectedOptionsObj);

    return options.map((_, optionIndex) => {
      const tiles = options[optionIndex].optionValues.map(optionValue => {
        for (
          let accuracy = options.length;
          accuracy > optionIndex;
          accuracy--
        ) {
          // Formulate an array of target options with given accuracy
          let targetOptions = selectedOptions.slice(0, accuracy);
          targetOptions[
            optionIndex
          ] = `${options[optionIndex].name}-${optionValue.name}`;

          // Attempt to find variant with target options
          const target = head(
            reduce(
              (acc, v) => intersection(acc, variantOptionsMap[v] || []),
              variantOptionsMap[targetOptions[0]] || [],
              targetOptions
            )
          );

          if (target) {
            // Target found, store accuracy and break
            return {
              tileLabel: optionValue.name,
              targetAccuracy: accuracy,
              targetVariantId: target,
            };
          }
        }

        // if not returned from the loop then return this value
        return {
          tileLabel: optionValue.name,
          targetAccuracy: 0,
          targetVariantId: null,
        };
      });
      return {
        label: options[optionIndex].name,
        tiles,
        selectedTile: selectedOptionsValues[optionIndex],
      };
    });
  };
};

export const getSelectedVariantId = variantOptionsMap => selectedOptions => {
  const allOptions = values(selectedOptions);
  const allVariantIds = compose(
    xs => reduce((acc, v) => intersection(acc, v), xs[0], xs),
    map(option => pathOr([], [option], variantOptionsMap))
  );

  return path([0], allVariantIds(allOptions));
};

export const getVariantsData = async (
  variantId,
  $getShopifyData,
  isQuickviewProduct = false
) => {
  const { data } = await $getShopifyData({
    query: variantById,
    variables: { id: getShopifyGlobalId(variantId), isQuickviewProduct },
  });

  return getVariant(data?.node);
};

export default async function (
  handle,
  variantId,
  $getShopifyData,
  isQuickviewProduct = false
) {
  const isVariantSelected = Boolean(variantId);
  const globalVariantId = getShopifyGlobalId(variantId);

  const { data: productData } = await $getShopifyData({
    query: productDetailByHandle,
    variables: {
      handle,
      variantId: globalVariantId,
      isVariantSelected,
      isQuickviewProduct,
    },
  });

  const response = productData?.product;
  if (!response) {
    return null;
  }

  const selectedVariant = isVariantSelected
    ? productData?.node
    : response?.firstVariant?.nodes?.[0];

  if (!selectedVariant) {
    return null;
  }

  // #region products processing
  const product = getProduct(response);
  const mvTag = product.tags.find(tag => tag.includes("mv:"));
  const opmf = product.tags.find(tag => tag.includes("opmf:"));

  let products = [];
  if (mvTag || opmf) {
    const { data } = await $getShopifyData({
      query: productsByTitle,
      variables: { query: mvTag ? `tag:'${mvTag}'` : `tag:'${opmf}'` },
    });
    products = data?.products?.nodes ?? [];
  } else {
    const { data } = await $getShopifyData({
      query: productById,
      variables: { productId: response.id },
    });
    products = data.node ? [data.node] : [];
  }

  const getOptions = compose(
    reduce(
      (acc, [name, optionValues]) => acc.concat({ name, optionValues }),
      []
    ),
    x => Object.entries(x),
    reduceBy(
      (acc, { optionValues }) => uniq(acc.concat(optionValues)),
      [],
      prop("name")
    )
  );
  const options = getOptions(products.flatMap(product => product.options));

  const variants = products
    .flatMap(product => product.productVariants.nodes)
    .map(getVariant);
  // #endregion

  return {
    ...product,
    options,
    selectedVariant: getVariant(selectedVariant),
    variants,
    campaign: {
      saleColor: product.product_campaign_badge_color,
      saleTitle: product.product_campaign_badge_title,
    },
  };
}

export function getSeoData({
  title: defaultTitle,
  brand_list: brandList,
  tags,
  seo,
}) {
  const createMetaTitle = () => {
    const metaTitle = seo?.title ?? defaultTitle;
    let title = "";
    if (brandList && !metaTitle.startsWith(brandList)) {
      title += `${brandList} `;
    }
    title += metaTitle;
    if (tags.includes("afterpay-bedroom-furniture")) {
      title += " | Afterpay Available";
    }
    title += " | Snooze";
    return title;
  };

  const metaDescription = seo?.description ?? "";

  return { metaTitle: createMetaTitle(), metaDescription };
}

const getComfortFeel = option => {
  const comfortFeels = [
    "Comfort Feel",
    "Comfort Feel Left",
    "Comfort Feel - Side One",
  ];

  return comfortFeels.reduce((result, feel) => {
    if (result) {
      return result;
    }
    return pathOr("", [feel, "value"], option);
  }, "");
};

export const getVariantTitle = (
  { variant, isMultiVariant },
  includeDash = true
) => {
  if (variant.title === "Default Title") {
    return "";
  }

  const { selectedOptions } = variant;
  const options = Array.isArray(selectedOptions)
    ? groupByProp("name")(selectedOptions)
    : selectedOptions;
  const size = options?.Size?.value ?? options?.size ?? "";

  if (isMultiVariant) {
    const colour = options?.Colour?.value ?? options?.colour ?? "";
    return ` / ${size} / ${colour}`;
  }

  const comfortFeel = getComfortFeel(options);
  if (hasSingleSize(options) && comfortFeel) {
    return `${includeDash ? " - " : ""}${size} / ${comfortFeel}`;
  }

  return `${includeDash ? " - " : ""}${variant.title}`;
};

export function isMattressAndBaseRemoval(tags = []) {
  return (
    tags.includes("mattresses") ||
    tags.includes("has-base") ||
    tags.includes("bedgear-mattress") ||
    tags.includes("base")
  );
}
